import BillingRule from "./billing-rule";
import Branch from "./branch";
import Client from "./client";
import ProjectBudget from "./project-budget";
import ProjectStatus from "./project-status";
import ProjectTask from "./project-task";
import Transaction from "./transaction";
import { Queryable } from "./queryable";
import RateTable from "./rate-table";
import Department from "./department";
import Terms from "./terms";
import ClientProjectFinance from "./project-finance";
import { FailingCheck, HealthStatus } from "./project-health";
import { FALLBACK_MARGIN_BANDS, MarginBands } from "./project-margin-target";

// Lite employee shape matching the API's projectManager select — not the full
// shared Employee model (that one still keys off the pre-refactor `employeeId` field).
export interface ProjectManagerLite {
  id: string
  firstName: string
  lastName: string
  commonName?: string | null
}

export default interface Project {
  id: string;
  code: string;
  projectManagerId?: string;
  clientId?: string;
  branchId?: string;
  departmentId?: string;
  billingId?: string;
  rateTableId?: string;
  termsId?: string;
  projectStatusId?: string;
  isTemplate?: boolean;
  description: string;
  createdAt: Date;
  updatedAt?: Date;
  startDate?: Date;
  endDate?: Date;
  graceDate?: Date;
  renewalBillingStartDate?: Date;
  syncedAt?: Date;

  // Navigation (ALL optional)
  status?: ProjectStatus;
  projectManager?: ProjectManagerLite;
  billing?: BillingRule;
  branch?: Branch;
  client?: Client;
  department?: Department;
  rateTable?: RateTable;
  terms?: Terms;

  tasks?: ProjectTask[]
  budgets?: ProjectBudget[]
  transactions?: Transaction[]
  projectFinances?: ClientProjectFinance[]
}

export interface ProjectForecast {
  code: string
  id: string
  budget: number
  revenue: number
  projectFinances: ClientProjectFinance[]
}

// Backs the Projects list — restores the old vw_Project view's shape (flat
// customer/approver/branch/subAccount/status + revenue/cost/margin), computed
// in ProjectModel.getAllWithMargin instead of a DB view.
export interface ProjectListItem {
  id: string
  code: string
  description: string
  customer: string | null
  customerId: string | null
  approver: string | null
  approverId: string | null
  branch: string | null
  branchId: string | null
  subAccount: string | null
  subAccountId: string | null
  status: string | null
  statusId: string | null
  startDate?: Date | null
  revenueActual: number
  costActual: number
  marginPercent: number
  // Project Health (null when the project is not Active)
  healthStatus?: HealthStatus | null
  failingChecks?: FailingCheck[]
}

export interface vw_ProjectProfitability {
  branchId: string
  branch: string
  projectCode: string
  projectId: string
  manager?: string
  managerId?: string
  customer?: string
  customerId?: string
  subAccountDescription: string
  subAccountLabel?: string
  subAccountId: string
  status: string
  statusId: string
  startDate?: Date

  aRAmount: number
  aRAccrual: number
  revenue: number
  aPAmount: number
  aPAccrual: number
  inventory: number
  timecard: number
  expenses: number
  margin: number
  marginPCT: number
}

// Fallback ideal margin only. The real values are per fiscal year in Admin > Project Margin Targets
// (useProjectMarginTarget / EffectiveMarginTarget).
export const DEFAULT_PROJECT_MARGIN_PERCENT = FALLBACK_MARGIN_BANDS.idealMargin

export class ProjectUI {
  static readonly ACTIVE_PROJECT_STATUS = 'Active'
  static readonly CANCELED_PROJECT_STATUS = 'Canceled'
  static readonly COMPLETED_PROJECT_STATUS = 'Completed'
  static readonly SUSPENDED_PROJECT_STATUS = 'Suspended'
  static readonly PLANNING_PROJECT_STATUS = 'In Planning'


  static statusColor(status?: string): string {

    if (!status) return ''

    if (status === this.ACTIVE_PROJECT_STATUS)
      return 'active'

    if (status === this.CANCELED_PROJECT_STATUS)
      return 'warning'

    if (status === this.COMPLETED_PROJECT_STATUS)
      return 'success'

    if (status === this.SUSPENDED_PROJECT_STATUS)
      return 'error'

    if (status === this.PLANNING_PROJECT_STATUS)
      return 'pending'

    return ''
  }


  // Same bands as Project Health, from the fiscal year's margin target (Admin > Project Margin Targets):
  // >= minimum green, >= warning yellow, below warning red. Without bands, the health rule defaults apply.
  static marginPercentColor(marginPercent: number, bands?: Pick<MarginBands, 'minimumMargin' | 'warningMargin'> | null): string {
    const { minimumMargin, warningMargin } = bands ?? FALLBACK_MARGIN_BANDS
    const value = Number(marginPercent)

    if (value >= minimumMargin)
      return "success-dark"

    if (value >= warningMargin)
      return "warning"

    return "error"
  }

  static marginColor(margin: number): string {
    if (margin < 0)
      return 'error'

    if (margin > 0)
      return 'success'

    return ''
  }

  static currencyValueColor(value: number): string {
    if (value < 0)
      return 'error'

    return ''
  }
}



export class ProjectFinance {
  readonly project: Project

  // Cost
  readonly expenseBudgetType = "Expense"

  // Revenue
  readonly incomeBudgetType = "Income"
  readonly assetBudgetType = "Asset"

  // Transaction accounts
  readonly consultingIncomeCode = "410000"
  readonly accruedRevenueCode = "410100"

  constructor(project: Project) {
    this.project = project
  }

  // ---------- Core helpers ----------
  private get transactions(): Transaction[] {
    return this.project.transactions ?? []
  }

  private get tasks(): ProjectTask[] {
    return this.project.tasks ?? []
  }


  private get budgets(): ProjectBudget[] {
    return this.project.budgets ?? []
  }

  private get revenueActual(): number {
    const revs = this.budgets.filter(b => b.projectBudgetType?.description === this.incomeBudgetType)
    let revsTotal = 0;
    revs.forEach(r => revsTotal += Number(r.actualAmount));
    return revsTotal;
  }

  private get costActual(): number {
    const cost = this.budgets.filter(b => b.projectBudgetType?.description === this.expenseBudgetType);
    let costTotal = 0;
    cost.forEach(c => costTotal += Number(c.actualAmount));
    return costTotal;
  }

  /**
   * This will calculte the cost with consideration of revised cost
   */
  get cost(): number {
    const cost = this.budgets.filter(b => b.projectBudgetType?.description === this.expenseBudgetType);
    let costTotal = 0;
    cost.forEach(c => {
      let res = Number(c.actualAmount);
      costTotal += res;
    })

    return costTotal;
  }

  /**
 * This will calculte the revenue with consideration of revised revenue
 */
  get revenue(): number {
    const revenue = this.budgets.filter(b => b.projectBudgetType?.description === this.incomeBudgetType);
    let revenueTotal = 0;
    revenue.forEach(r => {
      let res = Number(r.actualAmount);
      revenueTotal += res;
    })

    return revenueTotal;
  }



  get margin(): number {
    return this.revenueActual - this.costActual;
  }

  get marginPercent(): number {

    const revenue = this.revenueActual;

    if (revenue <= 0)
      return 0;

    return (this.margin / revenue) * 100
  }

  get budgetRemaing(): number[] {
    return [this.cost, this.revenue];
  }

  get accountsRecievable(): number {
    const trans = this.transactions.filter(t => t.transactionAccount?.code.trim() === this.consultingIncomeCode);


    let sum = 0;
    trans.forEach(t => sum += Number(t.amount) * -1);
    return sum
  }

  get accountsRecievableAccrual(): number {
    const trans = this.transactions.filter(t => t.transactionAccount?.code.trim() === this.accruedRevenueCode);

    let sum = 0;

    trans.forEach(t => sum += Number(t.amount) * -1);
    return sum
  }

  get financialAndForecasting(): ClientProjectFinance[] {

    const trans = this.transactions.filter((t: Transaction) => t.transactionAccount?.code.trim() === this.consultingIncomeCode || t.transactionAccount?.code.trim() === this.accruedRevenueCode);
    if (!this.project.projectFinances)
      return [];

    // Work on copies so the project's own rows are never mutated
    const projectFinances: ClientProjectFinance[] = this.project.projectFinances.map(pf => ({ ...pf }));

    const periodMap = new Map<number, Transaction[]>();

    for (let i = 0; i < trans.length; i++) {

      if (!trans[i].fiscalPeriod)
        continue;

      const key = trans[i].fiscalPeriod!;
      if (!periodMap.has(key)) {
        periodMap.set(key, []);
      }

      const arr = periodMap.get(key);
      arr!.push(trans[i])
      periodMap.set(key, arr!);
    }

    // Iterate over keys and add up actuals
    for (let i = 0; i < projectFinances.length; i++) {
      let actuals = 0;
      const t = periodMap.get(projectFinances[i].fiscalPeriod);

      t?.forEach(ts => {
        actuals += Number(ts.amount) * -1
      })
      projectFinances[i].actualAmount = actuals;
    }

    // Periods with actuals but no plan/forecast row used to be dropped. Show them with
    // planned/forecast 0; editing the forecast upserts the row like any other period.
    const projectId = this.project.id ?? projectFinances[0]?.projectId;
    if (projectId) {
      const known = new Set(projectFinances.map(pf => pf.fiscalPeriod));
      periodMap.forEach((rows, fiscalPeriod) => {
        if (known.has(fiscalPeriod)) return;
        const actuals = rows.reduce((sum, ts) => sum + Number(ts.amount) * -1, 0);
        if (actuals === 0) return;
        projectFinances.push({
          projectId,
          fiscalPeriod,
          plannedAmount: 0,
          forecastAmount: 0,
          actualAmount: actuals,
          createdById: '',
        });
      });
    }

    return projectFinances.sort((a: ClientProjectFinance, b: ClientProjectFinance) => a.fiscalPeriod - b.fiscalPeriod)
  }



  forecastForPeriod(fiscalPeriod: number): number {

    if (!this.project.projectFinances || this.project.projectFinances.length === 0)
      return 0;

    const pF = this.project.projectFinances.find(pF => pF.fiscalPeriod === fiscalPeriod);

    if (!pF) return 0;

    return pF.forecastAmount;
  }
}



export interface IProjectQueryParam extends Queryable {
  clientIds?: string[],
  statusIds?: string[],
  code?: string,
  projectManagerIds?: string[],
  branchIds?: string[],
  departmentIds?: string[]
  transactions?: boolean
  budgets?: boolean
  // Project Health filter: HealthStatus values
  healthStatuses?: string[]
}

export interface IProjectProfitabilityQueryParam extends IProjectQueryParam {
  fromPeriod?: number
  toPeriod?: number
  hideEmpty?: boolean
}
